/**
 * components/VersionGate.tsx
 * ------------------------------------------------------------------
 * Non-dismissible "new version" banner + the auto-reload driver
 * (useVersionGate). This is the SINGLE version banner in the app: the
 * old dismissible service-worker toast in PWAPrompt was removed in this
 * slice to avoid a double-banner. Mounted once at the app root, inside
 * DataProvider + DirtyProvider (it needs serverBuild and the dirty
 * registry).
 *
 * SAFETY (Aug 2026): a reload discards anything typed but NOT yet saved.
 * A teacher lost comments to a blind reload, so the Reload button now
 * WARNS (confirm) when any form holds unsaved work, and the copy no
 * longer implies unsaved work survives a reload.
 *
 * AT THE BOTTOM (Sep 2026), not across the top. The top bar sat over the
 * page furniture and read as an error; the school asked for it gone and for
 * the update to arrive the way the other prompts do — a toast at the bottom
 * that can be lived with while a piece of work is finished. It is still
 * non-dismissible, because a stale client silently failing to save is the
 * fault this exists to prevent.
 * ------------------------------------------------------------------
 */
import { RefreshCw } from 'lucide-react';
import { useVersionGate } from '@/hooks/useVersionGate';
import { useDirty } from '@/contexts/DirtyContext';
import { reloadToLatest } from '@/lib/swUpdate';

export default function VersionGate() {
  const { stale } = useVersionGate();
  const { hasUnsavedWork } = useDirty();
  if (!stale) return null;

  const onReload = () => {
    if (
      hasUnsavedWork() &&
      !window.confirm(
        'You have unsaved changes on this screen. They will be LOST if you reload now.\n\nClick Cancel, save your work first, then reload.',
      )
    ) {
      return;
    }
    reloadToLatest();
  };

  return (
    <div
      role="alert"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[100] animate-slide-up"
    >
      <div className="bg-[#1b3a6b] text-white rounded-2xl shadow-2xl px-4 py-3 flex items-start gap-3">
        <RefreshCw size={16} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">A new version is available</p>
          <p className="text-xs text-white/80 mt-0.5">
            Save your work first — reloading discards anything not yet saved.
          </p>
          <button
            onClick={onReload}
            className="mt-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold"
          >
            Reload now
          </button>
        </div>
      </div>
    </div>
  );
}
